import { useCallback, useState } from 'react'
import { Check, Circle, CheckCircle2, Clock } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'
const ALLOWED_STATUSES = ['todo', 'done']

const getStatusIcon = (status) => {
  if (status === 'done') return CheckCircle2
  if (status === 'in-progress' || status === 'in progress') return Clock
  return Circle
}

const getStatusIconColor = (status) => {
  if (status === 'done') return 'text-emerald-500'
  if (status === 'in-progress' || status === 'in progress') return 'text-blue-500'
  return 'text-muted-foreground'
}
const StatusCommandList = ({ existingStatuses, status, handleSelectStatus }) => (
  <CommandList>
    <CommandEmpty>
      <div className="px-3 py-2 text-xs text-muted-foreground">No status found.</div>
    </CommandEmpty>
    <CommandGroup heading="Statuses">
      {existingStatuses.map((s) => {
        const Icon = getStatusIcon(s)
        const iconColor = getStatusIconColor(s)
        return (
          <CommandItem key={s} value={s} onSelect={handleSelectStatus}>
            <Icon className={cn('h-4 w-4 mr-2', iconColor)} />
            <span>{s.charAt(0).toUpperCase() + s.slice(1)}</span>
            <Check className={cn('ml-auto h-4 w-4', status === s ? 'opacity-100' : 'opacity-0')} />
          </CommandItem>
        )
      })}
    </CommandGroup>
  </CommandList>
)
const StatusSelectorContent = ({
  inputValue,
  existingStatuses,
  status,
  handleSelectStatus,
  setInputValue,
}) => (
  <PopoverContent className="p-0 w-[min(200px,calc(100vw-1rem))]" align="start">
    <Command>
      <CommandInput
        placeholder="Change status..."
        value={inputValue}
        onValueChange={setInputValue}
      />
      <StatusCommandList
        existingStatuses={existingStatuses}
        status={status}
        handleSelectStatus={handleSelectStatus}
      />
    </Command>
  </PopoverContent>
)
export function StatusSelector({ status, onStatusChange, existingStatuses, size = 'default' }) {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const handleSelectStatus = useCallback(
    (currentValue) => {
      onStatusChange(currentValue)
      setOpen(false)
    },
    [onStatusChange],
  )
  const normalizedStatuses = Array.from(
    new Set([...ALLOWED_STATUSES, ...existingStatuses.filter((s) => ALLOWED_STATUSES.includes(s))]),
  )

  const Icon = getStatusIcon(status)
  const iconColor = getStatusIconColor(status)

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          className={cn(
            'h-5.5 w-auto gap-1 rounded-full border-0 bg-secondary/30 px-2 py-0 text-[10px] font-bold shadow-none hover:bg-secondary/60 focus:ring-0 transition-colors uppercase tracking-tight',
            size === 'sm' && 'h-5.5 px-2',
            size === 'xs' && 'h-5 px-1.5',
          )}
        >
          <Icon className={cn('h-3 w-3', iconColor)} />
          <span className="truncate max-w-[70px] sm:max-w-[92px]">
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </span>
        </Button>
      </PopoverTrigger>
      <StatusSelectorContent
        inputValue={inputValue}
        existingStatuses={normalizedStatuses}
        status={status}
        handleSelectStatus={handleSelectStatus}
        setInputValue={setInputValue}
      />
    </Popover>
  )
}
